/**
 * Calculator Grid Component (Material UI)
 * Displays all available calculators in a grid layout
 */
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useCustomTranslation } from '../../hooks';
import { calculators, calculatorCategories, type Calculator } from '../../data/calculators';

interface CalculatorGridMUIProps {
  showAll?: boolean;
  categoryFilter?: Calculator['category'];
  limit?: number;
}

export default function CalculatorGridMUI({ showAll = true, categoryFilter, limit }: CalculatorGridMUIProps) {
  const { t } = useCustomTranslation();

  let filteredCalculators = calculators;

  if (categoryFilter) {
    filteredCalculators = filteredCalculators.filter(calc => calc.category === categoryFilter);
  }

  if (limit) {
    filteredCalculators = filteredCalculators.slice(0, limit);
  }

  // Group by category for organized display
  const groupedByCategory = filteredCalculators.reduce((acc, calc) => {
    if (!acc[calc.category]) {
      acc[calc.category] = [];
    }
    acc[calc.category].push(calc);
    return acc;
  }, {} as Record<string, Calculator[]>);

  return (
    <Box id="calculator-suite" sx={{ py: { xs: 8, md: 10 }, scrollMarginTop: '80px', bgcolor: 'white' }}>
      <Container maxWidth="lg">
        {showAll && (
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 700,
              }}
            >
              {t('home.calculators.title')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              {t('home.calculators.subtitle')}
            </Typography>
          </Box>
        )}

        <Stack spacing={8}>
          {Object.entries(groupedByCategory).map(([category, calcs]) => (
            <Box key={category}>
              {showAll && (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                  }}
                >
                  <span style={{ fontSize: '2rem' }}>
                    {calculatorCategories[category as Calculator['category']].icon}
                  </span>
                  {t(calculatorCategories[category as Calculator['category']].titleKey)}
                </Typography>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {calcs.map((calculator) => (
                  <Box key={calculator.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderTop: 4,
                        borderColor: calculator.color,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      <CardActionArea
                        component={Link}
                        to={calculator.path}
                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 3 }}
                      >
                        {calculator.isNew && (
                          <Chip
                            label={t('home.calculators.new')}
                            color="success"
                            size="small"
                            sx={{ position: 'absolute', top: 16, right: 16 }}
                          />
                        )}
                        {calculator.comingSoon && (
                          <Chip
                            label={t('home.calculators.comingSoon')}
                            color="warning"
                            size="small"
                            sx={{ position: 'absolute', top: 16, right: 16 }}
                          />
                        )}

                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${calculator.color}20`,
                            mb: 2,
                          }}
                        >
                          <span style={{ fontSize: '32px' }}>{calculator.icon}</span>
                        </Box>

                        <CardContent sx={{ p: 0, flexGrow: 1, width: '100%' }}>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            {t(calculator.titleKey)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(calculator.descriptionKey)}
                          </Typography>
                        </CardContent>

                        <Box
                          sx={{
                            width: '100%',
                            pt: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: calculator.color, fontWeight: 600 }}>
                            {t('home.calculators.calculate')}
                          </Typography>
                          <ArrowForwardIcon sx={{ color: calculator.color, fontSize: 20 }} />
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
